import React from "react";
import { Controller, useForm } from "react-hook-form";
import { DropdownList } from "react-widgets";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import {
  AppPermissions,
  District,
  IGovernorate,
  useHasPermission,
} from "../../../../core";

const CreateForm: React.FunctionComponent<{
  onSubmit: (data: District) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  data?: District;
  error?: string;
  onDelete: (id?: string) => void;
  sourceList?: IGovernorate[];
}> = (props) => {
  const canDeleteDistrict = useHasPermission(AppPermissions.DeleteDistrict);
  const canCreateDistrict = useHasPermission(AppPermissions.CreateDistrict);
  const canUpdateDistrict = useHasPermission(AppPermissions.UpdateDistrict);
  const [isDeleteModalOpen, setDeleteModalOpen] = React.useState<boolean>(
    false
  );
  const { control, handleSubmit, errors } = useForm<District>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });
  const onDelete = () => {
    setDeleteModalOpen(true);
  };
  const onCancelDelete = () => {
    setDeleteModalOpen(false);
  };

  const renderEnglishTextBox = () => {
    return (
      <div className="col-lg-12 col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">District Name<span className="text-danger">*</span></label>
          <Controller
            control={control}
            rules={{ required: "Name is required" }}
            name="name"
            defaultValue={props.data?.name}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Name"
                name={name}
                value={value}
                onChange={onChange}
                id="name"
              />
            )}
          />
          <ErrorMessage>{errors.name?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderArabicTextBox = () => {
    return (
      <div className="col-lg-12 col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Name in arabic<span className="text-danger">*</span></label>
          <Controller
            control={control}
            rules={{ required: "Arabic name is required" }}
            name="translations.name.ar"
            defaultValue={props.data?.translations?.name?.ar}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                id="arabicName"
                placeholder="Arabic Name"
                name={name}
                value={value}
                onChange={onChange}
              />
            )}
          />
          <ErrorMessage>{errors?.translations?.name?.ar?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderGovernorate = () => {
    return (
      <div className="col-lg-12 col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Governorate</label>
          <Controller
            control={control}
            name="governorate"
            defaultValue={props.data?.governorate}
            render={({ onChange, value }) => (
              <DropdownList
                value={value}
                defaultValue={value}
                data={props.sourceList}
                textField={"name"}
                valueField={"name"}
                placeholder={"Select Governorate"}
                onChange={(val) => {
                  onChange(val.name);
                }}
              />
            )}
          />
          <ErrorMessage>{errors.governorate?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderDeleteConfirm = () => {
    return isDeleteModalOpen ? (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <p>
                        You are about to delete the District with name{" "}
                        {props.data?.name}
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-auto mr-auto">
                      <button
                        type="button"
                        className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
                        onClick={onCancelDelete}
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="col-auto">
                      <button
                        type="submit"
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
                        onClick={() => props.onDelete(props.data?._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    ) : (
      <></>
    );
  };

  const renderButton = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3 text-right pt-3">
          {props.data?._id && canDeleteDistrict && (
            <button
              type="button"
              className="btn btn-outline-gray py-2 font-weight-semibold font-normal px-4 mr-1"
              onClick={onDelete}
            >
              <i className="icon-bin-fill cursor-pointer align-self-center font-lg text-gray-700"></i>
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
            onClick={props.onCancel}
          >
            Cancel
          </button>
          {canCreateDistrict && !props.data?._id && (
            <button
              type="submit"
              className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
              disabled={props.isSubmitting}
            >
              Create
            </button>
          )}
          {canUpdateDistrict && props.data?._id && (
            <button
              type="submit"
              className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
              disabled={props.isSubmitting}
            >
              Update
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(props.onSubmit)}>
      <div className="p-4">
        <h4 className="mb-3 font-weight-semibold">
          {props.data && props.data._id ? "Update " : "Create New "} District
        </h4>
        <div className="row gutter-10">
          {renderEnglishTextBox()}
          {renderGovernorate()}
          <ErrorMessage>{props.error}</ErrorMessage>
          {renderButton()}
          {renderDeleteConfirm()}
        </div>
      </div>
    </form>
  );
};
export default CreateForm;
